"use client";

import React, { useState } from "react";
import { ArrowRight, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
interface FormDataState {
  name: string;
  phone: string;
  email: string;
  address: string;
  project_type: string;
  details: string;
  contact_method: string;
  timing: string;
  budget: string;
  consent: boolean;
}
const initialFormData: FormDataState = {
  name: "",
  phone: "",
  email: "",
  address: "",
  project_type: "",
  details: "",
  contact_method: "",
  timing: "",
  budget: "",
  consent: false
};
const relatedLinks = [{
  title: "Outdoor Living",
  to: "/outdoor-living",
  description: "Decks, covered porches, and outdoor entertainment spaces"
}, {
  title: "Remodeling Cost Guide",
  to: "/remodeling-cost-guide",
  description: "Detailed pricing frameworks and planning factors"
}, {
  title: "Service Areas",
  to: "/service-areas",
  description: "Communities we serve across Decatur and neighboring areas"
}];
const projectTypeOptions = ["Kitchen Remodeling", "Bathroom Remodeling", "Whole-Home Remodeling", "Home Additions", "Basement Remodeling", "Interior Remodeling", "Outdoor Living", "Deck Remodeling", "Other"];
const contactMethodOptions = ["Phone", "Text", "Email"];
const timingOptions = ["ASAP", "Within 1 month", "1-3 months", "3-6 months", "Just researching"];
const budgetOptions = ["Under $25,000", "$25,000-$50,000", "$50,000-$100,000", "$100,000+", "Not sure yet"];
export default function RelatedLinksEstimate() {
  const [formData, setFormData] = useState<FormDataState>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!formData.consent) {
      setErrorMessage("Please agree to the contact consent to proceed.");
      return;
    }
    setIsLoading(true);
    try {
      const pagePath = typeof window !== "undefined" ? window.location.pathname : "";
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("details", formData.details);
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget);
      params.append("consent", formData.consent ? "true" : "false");
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", pagePath);
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    } finally {
      setIsLoading(false);
    }
  };
  return <section id="relatedlinksestimateform" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        {/* Related Links */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedLinks.map((item, i) => <div key={item.to} data-index={i} className="group relative rounded-xl border border-border bg-card/80 p-6 shadow-xs transition-all hover:shadow-md hover:border-ring/40 hover:bg-card flex flex-col justify-between">
                <div className="space-y-2">
                  <h3 className="font-serif text-xl sm:text-2xl font-semibold tracking-tight text-card-foreground">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                    {item.description}
                  </p>
                </div>
                <div className="pt-4 mt-auto">
                  <Link to={item.to} className="inline-flex items-center text-sm font-medium text-foreground group-hover:text-chart-1 transition-colors">
                    <span>Explore</span>
                    <ArrowRight className="ml-1.5 size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>)}
          </div>
        </div>

        {/* Universal Estimate Form */}
        <div className="max-w-3xl mx-auto">
          <Card className="border border-border bg-card shadow-md rounded-xl">
            <CardHeader className="text-center space-y-2 pb-6 border-b border-border/60">
              <CardTitle className="font-serif text-2xl sm:text-3xl lg:text-4xl text-card-foreground font-semibold">
                Request an Estimate
              </CardTitle>
              <CardDescription className="font-sans text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
                Share a few details about your renovation goals to schedule an initial consultation and review pricing.
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-8">
              {isSuccess ? <div className="py-12 px-4 text-center space-y-4">
                  <div className="inline-flex items-center justify-center size-14 rounded-full bg-secondary text-foreground mx-auto">
                    <CheckCircle2 className="size-8 text-foreground" />
                  </div>
                  <h3 className="font-serif text-2xl font-semibold text-foreground">
                    Request Received
                  </h3>
                  <p className="text-muted-foreground font-sans max-w-md mx-auto text-sm sm:text-base">
                    Thank you for reaching out. We have received your project details and will contact you via your preferred method shortly.
                  </p>
                </div> : <form onSubmit={handleSubmit} className="space-y-6 font-sans">
                  {errorMessage && <Alert variant="destructive" className="rounded-lg">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Submission Error</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-name">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-name" type="text" required placeholder="John Smith" value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-phone">
                        Phone Number <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-phone" type="tel" required placeholder="Your phone number" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-email">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-email" type="email" required placeholder="Your email address" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} />
                    </div>

                    {/* Address */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-address">
                        Street Address or ZIP Code{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input id="estimate-address" type="text" required placeholder="Decatur, GA 30030" value={formData.address} onChange={e => setFormData({
                    ...formData,
                    address: e.target.value
                  })} />
                    </div>
                  </div>

                  {/* Project Type */}
                  <div className="space-y-2">
                    <Label htmlFor="estimate-project-type">
                      Project Type <span className="text-destructive">*</span>
                    </Label>
                    <Select required value={formData.project_type} onValueChange={val => setFormData({
                  ...formData,
                  project_type: val
                })}>
                      <SelectTrigger id="estimate-project-type" className="w-full">
                        <SelectValue placeholder="Select project type" />
                      </SelectTrigger>
                      <SelectContent>
                        {projectTypeOptions.map(opt => <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-2">
                    <Label htmlFor="estimate-details">Project Details</Label>
                    <Textarea id="estimate-details" placeholder="Tell us about the scope, architectural style, or key goals for your home." rows={4} value={formData.details} onChange={e => setFormData({
                  ...formData,
                  details: e.target.value
                })} />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    {/* Preferred Contact Method */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-contact-method">
                        Preferred Contact Method{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Select required value={formData.contact_method} onValueChange={val => setFormData({
                    ...formData,
                    contact_method: val
                  })}>
                        <SelectTrigger id="estimate-contact-method" className="w-full">
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                        <SelectContent>
                          {contactMethodOptions.map(opt => <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Project Timing */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-timing">
                        Project Timing <span className="text-destructive">*</span>
                      </Label>
                      <Select required value={formData.timing} onValueChange={val => setFormData({
                    ...formData,
                    timing: val
                  })}>
                        <SelectTrigger id="estimate-timing" className="w-full">
                          <SelectValue placeholder="Select timing" />
                        </SelectTrigger>
                        <SelectContent>
                          {timingOptions.map(opt => <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Estimated Budget */}
                    <div className="space-y-2">
                      <Label htmlFor="estimate-budget">Estimated Budget</Label>
                      <Select value={formData.budget} onValueChange={val => setFormData({
                    ...formData,
                    budget: val
                  })}>
                        <SelectTrigger id="estimate-budget" className="w-full">
                          <SelectValue placeholder="Select budget range" />
                        </SelectTrigger>
                        <SelectContent>
                          {budgetOptions.map(opt => <SelectItem key={opt} value={opt}>
                              {opt}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Consent Checkbox */}
                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox id="estimate-consent" required checked={formData.consent} onCheckedChange={checked => setFormData({
                  ...formData,
                  consent: checked === true
                })} />
                    <div className="grid gap-1.5 leading-none">
                      <label htmlFor="estimate-consent" className="text-sm font-normal text-muted-foreground cursor-pointer">
                        I agree to be contacted about my project by phone, text, or email.{" "}
                        <span className="text-destructive">*</span>
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <Button type="submit" size="lg" disabled={isLoading} className="w-full text-base font-medium transition-all">
                      {isLoading ? <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Sending Request...
                        </> : "Submit Estimate Request"}
                    </Button>
                  </div>
                </form>}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}